import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { apiClient } from '../../api/client';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({
    type: null,
    text: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setLoading(true);
    setMessage({ type: null, text: '' });

    try {
      const response = await apiClient.post('/api/auth/request-reset', {
        email: email.trim()
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'If an account with that email exists, a password reset link has been sent.'
        });
        // Clear the form
        setEmail('');
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Failed to send reset email'
        });
      }
    } catch (error: any) {
      console.error('Password reset request failed:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'An error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Login Link */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>

        <Card className="bg-card backdrop-blur-sm border-coral-500/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-coral-500/20 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-coral-700 dark:text-foreground-muted" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Forgot Password
            </CardTitle>
            <p className="text-foreground-secondary mt-2">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Status Message */}
            {message.type && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-300'
                  : 'bg-red-500/10 border border-red-500/20 text-red-300'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm">{message.text}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-coral-700 dark:text-foreground-muted mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-coral-600 dark:text-coral-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full pl-10 pr-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-coral-500/40 focus:border-coral-500/40 transition-colors"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-coral-500/40"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Sending Reset Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>

            {/* Additional Info */}
            <div className="text-center text-sm text-foreground-muted space-y-2">
              <p>
                Remember your password?{' '}
                <Link to="/login" className="text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground transition-colors">
                  Sign in here
                </Link>
              </p>
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground transition-colors">
                  Sign up
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Note */}
        <div className="mt-6 text-center text-xs text-foreground-muted">
          <p>
            For security reasons, we don't indicate whether an email address is registered with us.
            If you don't receive an email, please check your spam folder or contact support.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;